import { ColDef, IFilterParams } from 'ag-grid-community'
import { IconCellRenderer } from '../../../components/cellRenderer'
import {
  EntitySearchReferenceEntity,
  WbsItemAdditionalPropertyEntity,
} from '../../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import {
  ReferenceEntityValueType,
  wbsItemAdditionalPropertyValuesVoService,
} from '../../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { Option } from '..'
import { EntitySearchCellEditor } from '../../../components/cellEditor'
import { filter } from '../../../../../pages/ProjectPlanNew/projectPlanNew'
import {
  ClientSideSelectFilter,
  SelectFilter,
  ServerSideSelectFilter,
} from '../../../components/filter'
import {
  EntitySearchReferenceEntityRepository,
  GetEntitySearchReferenceEntityRepository,
} from '../../../../../hooks/useWbsItemAdditionalProperties'
import { ColumnType } from '../../../../commons/AgGrid'
import { ReferencedEntity } from '../../../../../../domain/value-object/ReferencedEntity'
import { WbsItemAdditionalPropertyRepository } from '../../../../../../applications/ports/wbsItemAdditionalPropertyRepository'

export const entitySearchColumnDef = <RowData>(
  column: ColDef<RowData>,
  projectUuid: string,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  wbsItemAdditionalPropertyRepository: WbsItemAdditionalPropertyRepository,
  option: Option<RowData>,
  getEntitySearchRepositories: GetEntitySearchReferenceEntityRepository
): void => {
  const repository = getEntitySearchRepositories(
    wbsItemAdditionalProperty.entitySearchReferenceEntity!,
    wbsItemAdditionalProperty.entitySearchReferenceEntityTicketType
  )
  column.type = [ColumnType.autocomplete]
  column.cellRenderer = IconCellRenderer
  column.cellRendererParams = {
    ...column.cellRendererParams,
    getLabelWithIconUrl: (
      rowData: RowData
    ):
      | {
          label: string
          iconUrl: string
        }
      | undefined => {
      const wbsItemAdditionalPropertyValues =
        option.getAdditionalPropertyValues(rowData)
      if (!wbsItemAdditionalPropertyValues) return undefined
      const wbsItemAdditionalPropertyValue =
        wbsItemAdditionalPropertyValuesVoService.getValue(
          wbsItemAdditionalPropertyValues,
          wbsItemAdditionalProperty.uuid
        )
      if (!wbsItemAdditionalPropertyValue) {
        return undefined
      }
      const referenceEntityValue =
        wbsItemAdditionalPropertyValue as ReferenceEntityValueType
      return {
        // @ts-ignore
        label: referenceEntityValue.displayName || referenceEntityValue.name,
        iconUrl: referenceEntityValue.iconUrl,
      }
    },
  }
  column.cellEditor = EntitySearchCellEditor
  column.suppressKeyboardEvent = params =>
    (!params.editing && ['Delete', 'Backspace'].includes(params.event.key)) ||
    (params.editing && params.event.key === 'Enter')
  column.comparator = (
    value1?: ReferencedEntity,
    value2?: ReferencedEntity
  ) => {
    // @ts-ignore
    const name1 = value1?.displayName || value1?.name
    // @ts-ignore
    const name2 = value2?.displayName || value2?.name
    return (name1 ?? '').localeCompare(name2 ?? '')
  }

  if (
    isCachedAllInContext(wbsItemAdditionalProperty.entitySearchReferenceEntity!)
  ) {
    column.cellEditorParams = {
      entity: toBulkSheetEntity(
        wbsItemAdditionalProperty.entitySearchReferenceEntity!
      ),
    }
  } else {
    column.cellEditorParams = {
      searchCacheKey: toEntitySearchReferenceEntityCacheKey(
        wbsItemAdditionalProperty.entitySearchReferenceEntity!,
        wbsItemAdditionalProperty.entitySearchReferenceEntityTicketType
      ),
      search: (text: string, data) => repository.search(text),
    }
  }
  setFilterParams(
    column,
    wbsItemAdditionalProperty,
    wbsItemAdditionalPropertyRepository,
    option,
    repository
  )
}

const setFilterParams = <RowData>(
  column: ColDef<RowData>,
  wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
  wbsItemAdditionalPropertyRepository: WbsItemAdditionalPropertyRepository,
  option: Option<RowData>,
  referenceEntityRepository: EntitySearchReferenceEntityRepository
) => {
  const projectUuid = wbsItemAdditionalProperty.projectUuid
  switch (option.columnFilterType) {
    case 'CLIENT_SIDE':
      column.filter = ClientSideSelectFilter
      column.floatingFilter = !option.disableFloatingFilter
      column.filterParams = {
        valueGetter: params => {
          if (!params.node.data) return undefined

          const wbsItemAdditionalPropertyValues =
            option.getAdditionalPropertyValues(params.node.data)
          if (!wbsItemAdditionalPropertyValues) return undefined
          const wbsItemAdditionalPropertyValue =
            wbsItemAdditionalPropertyValuesVoService.getValue(
              wbsItemAdditionalPropertyValues,
              wbsItemAdditionalProperty.uuid
            )
          if (!wbsItemAdditionalPropertyValue) {
            return undefined
          }
          return wbsItemAdditionalPropertyValue as ReferenceEntityValueType
        },
        getValue: (option: ReferenceEntityValueType) => {
          return option?.uuid
        },
        getLabel: (option: ReferenceEntityValueType) => {
          // @ts-ignore
          return option?.displayName || option?.name
        },
      }
      break
    case 'SERVER_SIDE':
      column.filter = ServerSideSelectFilter
      column.floatingFilter = !option.disableFloatingFilter
      column.filterParams = {
        fetchOptions: async (params: IFilterParams) =>
          wbsItemAdditionalPropertyRepository.fetchColumnFilterOptions(
            wbsItemAdditionalProperty.uuid,
            params.context.rootProjectPlan?.uuid // TODO Re-consider implementing
          ),
        getValue: option => option.uuid,
        getLabel: option => option.displayName || option.name,
        fetch: (v: SelectFilter) => {
          return filter({
            projectUuid: wbsItemAdditionalProperty.projectUuid,
            additionalPropertyValues: [
              {
                wbsItemAdditionalPropertyUuid: wbsItemAdditionalProperty.uuid,
                propertyType: wbsItemAdditionalProperty.propertyType,
                values: v.values,
                includeBlank: v.includeBlank,
              },
            ],
          })
        },
      }
      break
    default:
      break
  }
}

export const toBulkSheetEntity = (
  entitySearchReferenceEntity: EntitySearchReferenceEntity
): string | undefined => {
  switch (entitySearchReferenceEntity) {
    case EntitySearchReferenceEntity.TEAM:
      return 'team'
    case EntitySearchReferenceEntity.PROJECT_MEMBER:
      return 'member'
    default:
      return undefined
  }
}

export const toEntitySearchReferenceEntityCacheKey = (
  entitySearchReferenceEntity: EntitySearchReferenceEntity,
  entitySearchReferenceEntityTicketType?: string
): string | undefined => {
  switch (entitySearchReferenceEntity) {
    case EntitySearchReferenceEntity.PROCESS:
    case EntitySearchReferenceEntity.DELIVERABLE_LIST:
    case EntitySearchReferenceEntity.DELIVERABLE:
    case EntitySearchReferenceEntity.TEAM:
      return entitySearchReferenceEntity
    case EntitySearchReferenceEntity.TICKET:
      return `ticket:${entitySearchReferenceEntityTicketType}`
    default:
      return undefined
  }
}
export const isTicketEntityCacheKey = (cacheKey: string): boolean => {
  return cacheKey.startsWith('ticket:')
}
export const getTicketTypeFromCacheKey = (cacheKey: string): string => {
  return cacheKey.replace('ticket:', '')
}

const isCachedAllInContext = (
  entitySearchReferenceEntity: EntitySearchReferenceEntity
): boolean => {
  return (
    EntitySearchReferenceEntity.TEAM === entitySearchReferenceEntity ||
    EntitySearchReferenceEntity.PROJECT_MEMBER === entitySearchReferenceEntity
  )
}
